import { AlertCircle, ImageIcon } from "lucide-react";
import React from "react";

import { ImagesViewProps } from "./types";

export const ImagesView: React.FC<ImagesViewProps> = ({
  images,
  loading,
  error,
  onCitationClick,
  onImageLoadError,
}) => {
  const safeImages = Array.isArray(images) ? images : [];

  if (loading && safeImages.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square animate-pulse rounded-xl bg-neutral-100 shadow-sm"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border border-neutral-200 bg-card p-8 text-center shadow-sm">
        <div className="mb-4 rounded-full bg-destructive-subtle p-3 text-destructive-text">
          <AlertCircle size={28} />
        </div>
        <h3 className="text-lg font-semibold text-neutral-900">Failed to load images</h3>
        <p className="mt-2 max-w-xs text-sm text-neutral-500">{error}</p>
      </div>
    );
  }

  if (safeImages.length === 0) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded-2xl border border-border bg-card p-12 text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-neutral-50 text-neutral-400">
          <ImageIcon size={40} />
        </div>
        <h3 className="text-xl font-bold text-neutral-900">No images found</h3>
        <p className="mt-3 max-w-sm text-base text-neutral-500">
          This case doesn&apos;t have any image files associated with it yet.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {safeImages.map((img) => (
        <div
          key={img.image_id}
          className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 shadow-sm transition-all duration-300 hover:border-neutral-300 hover:shadow-xl"
          onClick={() => onCitationClick(String(img.file_id), [img.page_number ?? 1])}
        >
          {img.url ? (
            <>
              <img
                src={img.url}
                alt={img.file_name}
                className="h-full w-full object-cover"
                loading="lazy"
                onError={() => onImageLoadError(img.image_id)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 transition-all duration-300 group-hover:opacity-100">
                <p className="truncate text-2xs font-medium text-white shadow-sm">
                  {img.file_name}
                </p>
              </div>
            </>
          ) : (
            <div className="relative flex h-full w-full flex-col bg-neutral-100">
              <div className="flex flex-1 items-center justify-center">
                <div className="animate-pulse">
                  <ImageIcon className="h-10 w-10 text-neutral-300" />
                </div>
              </div>
              <div className="border-t border-neutral-200 bg-card px-3 py-2">
                <p className="truncate text-2xs font-medium text-neutral-900">{img.file_name}</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
